import React, { useState } from "react";
import { X, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatErr } from "../api";

/**
 * LeadModal — public "Talk to Sales" form.
 * Renders when `open` is true. Calls `onClose` when the user dismisses or the
 * submission succeeds. Source lets us attribute leads to the CTA that triggered
 * them (landing_nav / landing_hero / pricing / final_cta).
 */
export default function LeadModal({ open, onClose, source = "landing", title = "Talk to Sales" }) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/leads", {
        name: name.trim(), company: company.trim(),
        email: email.trim().toLowerCase(), phone: phone.trim(),
        message: message.trim(), source,
      });
      setSent(true);
      toast.success("Thanks — our team will be in touch.");
      setTimeout(() => { close(); }, 1400);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setName(""); setCompany(""); setEmail(""); setPhone(""); setMessage("");
    setSent(false); setBusy(false);
    onClose && onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         onClick={close} data-testid="lead-modal">
      <div onClick={e => e.stopPropagation()}
           className="w-full max-w-lg rounded-3xl shadow-2xl relative"
           style={{ background: "#ffffff", color: "#0B1220" }}>
        <button onClick={close} data-testid="lead-modal-close"
                className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full hover:bg-black/5 transition">
          <X size={16} weight="bold" />
        </button>

        {sent ? (
          <div className="p-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-[#22D3EE] to-[#A3E635] grid place-items-center">
              <CheckCircle size={26} weight="fill" color="white" />
            </div>
            <div className="mt-4 text-2xl font-black tracking-tight">You&apos;re in the queue.</div>
            <div className="mt-2 text-sm" style={{ color: "#4B5563" }}>
              Someone from our team will reach out within one business day.
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 sm:p-8 pb-2">
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: "#4D7C0F" }}>
                Creator Operations Platform
              </div>
              <div className="mt-2 text-2xl sm:text-3xl font-black tracking-tight" data-testid="lead-modal-title">
                {title}
              </div>
              <div className="mt-2 text-sm" style={{ color: "#4B5563" }}>
                Tell us a bit about your team and we&apos;ll set up a 30-minute walkthrough with real data.
              </div>
            </div>

            <form onSubmit={submit} className="p-6 sm:p-8 pt-4 space-y-3">
              <Row>
                <Field label="Name*" testid="lead-name">
                  <input required value={name} onChange={e => setName(e.target.value)}
                         placeholder="Aria Mehta" className="lead-input" data-testid="lead-input-name" />
                </Field>
                <Field label="Company" testid="lead-company">
                  <input value={company} onChange={e => setCompany(e.target.value)}
                         placeholder="Studio Nova" className="lead-input" data-testid="lead-input-company" />
                </Field>
              </Row>
              <Row>
                <Field label="Work email*" testid="lead-email">
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                         placeholder="you@company.com" className="lead-input" data-testid="lead-input-email" />
                </Field>
                <Field label="Phone" testid="lead-phone">
                  <input value={phone} onChange={e => setPhone(e.target.value)}
                         placeholder="+91 98765 43210" className="lead-input" data-testid="lead-input-phone" />
                </Field>
              </Row>
              <Field label="Message" testid="lead-message">
                <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)}
                          placeholder="How many creators does your team manage today? What's the biggest ops bottleneck?"
                          className="lead-input" data-testid="lead-input-message" />
              </Field>

              <button type="submit" disabled={busy} data-testid="lead-submit"
                      className="w-full mt-2 py-3 rounded-full font-bold text-white flex items-center justify-center gap-2 transition"
                      style={{ background: "linear-gradient(90deg, #22D3EE 0%, #8B5CF6 100%)",
                               boxShadow: "0 10px 28px rgba(34,211,238,0.35)",
                               opacity: busy ? 0.7 : 1 }}>
                {busy ? "Sending…" : "Send message"} <PaperPlaneTilt size={14} weight="fill" />
              </button>
              <p className="text-[11px] text-center pt-1" style={{ color: "#6B7280" }}>
                By submitting you agree to be contacted by the CrewHQ team. We reply within one business day.
              </p>
            </form>
          </>
        )}
      </div>

      {/* Scoped input styles — matches the light modal aesthetic. */}
      <style>{`
        .lead-input {
          width: 100%; padding: 10px 12px; border-radius: 12px;
          border: 1px solid rgba(11,18,32,0.12); background: #F8FAFC; color: #0B1220;
          outline: none; font-size: 14px;
          transition: border-color 120ms ease, background 120ms ease;
        }
        .lead-input:focus { border-color: #22D3EE; background: #ffffff; }
        .lead-input::placeholder { color: #94A3B8; }
      `}</style>
    </div>
  );
}

const Row = ({ children }) => <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider font-bold mb-1.5" style={{ color: "#4B5563" }}>{label}</div>
    {children}
  </label>
);
